import { useEffect, useState } from "react";
import { Link, Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ClipboardList, Bed, Sliders, CalendarClock, Moon, Mail, Trash2, Plus, RotateCcw, Euro, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AnswersReadable from "@/components/AnswersReadable";

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const nav = useNavigate();

    const doLogout = () => { logout(); nav("/admin/login"); };

    const linkCls = ({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
            isActive ? "bg-[#2A84C8] text-white" : "text-brand-ink/70 hover:bg-brand-beige"
        }`;

    return (
        <div className="min-h-screen bg-brand-beige flex" data-testid="admin-dashboard">
            <aside className="w-64 bg-white border-r border-brand-ink/10 flex flex-col p-4 sticky top-0 h-screen">
                <div className="flex items-center gap-2 mb-6 px-2">
                    <div className="w-9 h-9 rounded-xl bg-[#2A84C8] flex items-center justify-center">
                        <Moon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <p className="font-heading font-semibold text-brand-ink text-sm leading-tight">Le Petit Matelassier</p>
                        <p className="text-xs text-brand-ink/50">Espace conseiller</p>
                    </div>
                </div>
                <nav className="space-y-1 flex-1">
                    <NavLink to="/admin/dashboard/questionnaires" className={linkCls} data-testid="nav-questionnaires">
                        <ClipboardList className="w-4 h-4" /> Questionnaires
                    </NavLink>
                    <NavLink to="/admin/dashboard/mattresses" className={linkCls} data-testid="nav-mattresses">
                        <Bed className="w-4 h-4" /> Matelas
                    </NavLink>
                    <NavLink to="/admin/dashboard/coefficients" className={linkCls} data-testid="nav-coefficients">
                        <Sliders className="w-4 h-4" /> Coefficients
                    </NavLink>
                    <NavLink to="/admin/dashboard/pricing" className={linkCls} data-testid="nav-pricing">
                        <Euro className="w-4 h-4" /> Tarifs
                    </NavLink>
                    <NavLink to="/admin/dashboard/notifications" className={linkCls} data-testid="nav-notifications">
                        <Send className="w-4 h-4" /> Notifications
                    </NavLink>
                    <NavLink to="/admin/dashboard/appointments" className={linkCls} data-testid="nav-appointments">
                        <CalendarClock className="w-4 h-4" /> Rendez-vous
                    </NavLink>
                </nav>
                <div className="border-t border-brand-ink/10 pt-4">
                    <p className="text-xs text-brand-ink/50 px-2">{user?.email}</p>
                    <button onClick={doLogout} className="flex items-center gap-2 text-sm text-brand-ink/60 hover:text-red-600 px-2 mt-2" data-testid="logout-btn">
                        <LogOut className="w-4 h-4" /> Déconnexion
                    </button>
                    <Link to="/" className="block text-xs text-brand-ink/40 hover:text-[#2A84C8] px-2 mt-3">
                        Voir le questionnaire client →
                    </Link>
                </div>
            </aside>
            <main className="flex-1 p-8 overflow-auto">
                <Routes>
                    <Route index element={<QuestionnairesList />} />
                    <Route path="questionnaires" element={<QuestionnairesList />} />
                    <Route path="questionnaires/:qid" element={<QuestionnaireDetail />} />
                    <Route path="mattresses" element={<MattressesAdmin />} />
                    <Route path="coefficients" element={<CoefficientsAdmin />} />
                    <Route path="pricing" element={<PricingAdmin />} />
                    <Route path="notifications" element={<NotificationsAdmin />} />
                    <Route path="appointments" element={<AppointmentsList />} />
                </Routes>
            </main>
        </div>
    );
}

function QuestionnairesList() {
    const [list, setList] = useState([]);
    const [selected, setSelected] = useState(null);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [testSessionActive, setTestSessionActive] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    // Reclassify-as-test bulk action: track selected QIDs and the confirmation modal.
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [confirmReclassify, setConfirmReclassify] = useState(false);
    const [reclassifying, setReclassifying] = useState(false);

    const reload = () => {
        const params = new URLSearchParams({ filter });
        if (search.trim()) params.set("q", search.trim());
        if (statusFilter) params.set("status", statusFilter);
        api.get(`/admin/questionnaires?${params}`).then((r) => {
            setList(r.data);
            // Clear selection whenever the filtered list changes: a QID that
            // disappears from the view must never remain in `selectedIds`.
            setSelectedIds(new Set());
        }).catch((e) => toast.error(formatApiError(e)));
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter, statusFilter]);
    // Debounced search
    useEffect(() => {
        const t = setTimeout(() => reload(), 350);
        return () => clearTimeout(t);
        /* eslint-disable-next-line */
    }, [search]);
    useEffect(() => {
        api.get("/test_session/status").then((r) => setTestSessionActive(!!r.data?.active)).catch(() => {});
    }, []);

    const startTest = async () => {
        try {
            await api.post("/admin/test_session/start");
            setTestSessionActive(true);
            window.dispatchEvent(new Event("pm:test-session-changed"));
            toast.success("Session de test activée. Aucun email réel ne sera envoyé.");
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const stopTest = async () => {
        try {
            await api.post("/admin/test_session/stop");
            setTestSessionActive(false);
            window.dispatchEvent(new Event("pm:test-session-changed"));
            toast.success("Session de test terminée.");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const testDocs = list.filter((q) => q.is_test);

    // Only non-test rows are eligible for reclassification. Test rows get a
    // disabled checkbox so the UI remains predictable.
    const reclassifiable = list.filter((q) => !q.is_test);
    const allReclassifiableSelected = reclassifiable.length > 0 &&
        reclassifiable.every((q) => selectedIds.has(q.id));

    const toggleOne = (qid) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(qid)) next.delete(qid); else next.add(qid);
            return next;
        });
    };
    const selectAllFiltered = () => {
        setSelectedIds(new Set(reclassifiable.map((q) => q.id)));
    };
    const clearSelection = () => setSelectedIds(new Set());

    const selectedDocs = list.filter((q) => selectedIds.has(q.id));

    const doReclassify = async () => {
        setReclassifying(true);
        try {
            const ids = Array.from(selectedIds);
            const { data } = await api.post("/admin/questionnaires/reclassify-as-test", {
                questionnaire_ids: ids,
            });
            toast.success(
                `${data.reclassified_questionnaires} questionnaire(s) reclassé(s) en TEST` +
                (data.reclassified_appointments ? ` · ${data.reclassified_appointments} rendez-vous associé(s) également` : "")
            );
            setConfirmReclassify(false);
            clearSelection();
            reload();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setReclassifying(false); }
    };

    const deleteAllTestDocs = async () => {
        try {
            const q_ids = testDocs.map((q) => q.id);
            const { data } = await api.post("/admin/test_data/delete", { questionnaire_ids: q_ids, appointment_ids: [] });
            toast.success(`${data.deleted_questionnaires} diagnostic(s) de test supprimé(s).`);
            setConfirmDelete(false);
            reload();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div>
            <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
                <h1 className="font-heading text-3xl font-semibold text-brand-ink">Questionnaires</h1>
                <div className="flex items-center gap-2">
                    {testSessionActive ? (
                        <button onClick={stopTest} className="text-sm px-3 py-2 rounded-xl bg-amber-100 border border-amber-300 text-amber-900 hover:bg-amber-200" data-testid="stop-test-session-btn">
                            Quitter le mode TEST
                        </button>
                    ) : (
                        <button onClick={startTest} className="text-sm px-3 py-2 rounded-xl bg-amber-500 text-white hover:bg-amber-600" data-testid="start-test-session-btn">
                            Démarrer une session de test
                        </button>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher (prénom, nom, email, téléphone…)"
                    aria-label="Rechercher un client"
                    className="flex-1 min-w-[240px] min-h-[44px] rounded-xl border border-brand-ink/15 px-3 bg-white text-sm"
                    data-testid="search-input"
                />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filtrer par statut"
                    className="min-h-[44px] rounded-xl border border-brand-ink/15 px-3 bg-white text-sm"
                    data-testid="status-filter">
                    <option value="">Tous les statuts</option>
                    <option value="nouveau">Nouveau diagnostic</option>
                    <option value="a_contacter">À contacter</option>
                    <option value="rdv_demande">Rendez-vous demandé</option>
                    <option value="rdv_confirme">Rendez-vous confirmé</option>
                    <option value="essai_realise">Essai réalisé</option>
                    <option value="vente_conclue">Vente conclue</option>
                    <option value="sans_suite">Sans suite</option>
                </select>
                {[
                    { v: "all",  l: "Tous" },
                    { v: "real", l: "Réels" },
                    { v: "test", l: "TEST" },
                ].map((f) => (
                    <button key={f.v} onClick={() => setFilter(f.v)}
                        aria-pressed={filter === f.v}
                        className={`text-sm px-3 py-1.5 rounded-full border ${filter === f.v ? "bg-[#2A84C8] text-white border-[#2A84C8]" : "bg-white text-brand-ink border-brand-ink/15"}`}
                        data-testid={`filter-${f.v}`}>
                        {f.l}
                    </button>
                ))}
                {testDocs.length > 0 && (
                    <button onClick={() => setConfirmDelete(true)} className="ml-auto text-sm px-3 py-1.5 rounded-full border border-red-200 text-red-700 bg-red-50 hover:bg-red-100" data-testid="delete-test-data-btn">
                        Supprimer les {testDocs.length} diagnostic(s) TEST
                    </button>
                )}
            </div>
            {/* Bulk reclassify toolbar — visible only when something is actionable. */}
            {reclassifiable.length > 0 && (
                <div className="flex items-center gap-2 mb-4 flex-wrap bg-amber-50/60 border border-amber-200 rounded-xl px-3 py-2">
                    <span className="text-sm text-amber-900">
                        <strong>{selectedIds.size}</strong> sélectionné(s)
                        {selectedIds.size > 0 && <> sur {reclassifiable.length} reclassables</>}
                    </span>
                    <button onClick={selectAllFiltered}
                        className="text-sm px-3 py-1.5 rounded-full border border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
                        data-testid="select-all-filtered-btn">
                        Sélectionner tous les résultats filtrés ({reclassifiable.length})
                    </button>
                    {selectedIds.size > 0 && (
                        <button onClick={clearSelection}
                            className="text-sm px-3 py-1.5 rounded-full border border-brand-ink/15 bg-white hover:bg-brand-beige"
                            data-testid="clear-selection-btn">
                            Tout désélectionner
                        </button>
                    )}
                    <button
                        onClick={() => setConfirmReclassify(true)}
                        disabled={selectedIds.size === 0}
                        className="ml-auto text-sm px-3 py-1.5 rounded-full border border-amber-600 bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="reclassify-as-test-btn">
                        Reclasser en TEST ({selectedIds.size})
                    </button>
                </div>
            )}
            <div className="bg-white rounded-2xl border border-brand-ink/10 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-brand-beige text-brand-ink/70">
                        <tr>
                            <Th>
                                <input type="checkbox"
                                    aria-label="Tout sélectionner (reclassables)"
                                    checked={allReclassifiableSelected}
                                    onChange={(e) => e.target.checked ? selectAllFiltered() : clearSelection()}
                                    disabled={reclassifiable.length === 0}
                                    data-testid="select-all-header-cb" />
                            </Th>
                            <Th>Date</Th><Th>Nom</Th><Th>Contact</Th><Th>Profil</Th><Th>Matelas</Th><Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody data-testid="questionnaires-table-body">
                        {list.map((q) => (
                            <tr key={q.id} className="border-t border-brand-ink/5 hover:bg-brand-beige/50">
                                <Td>
                                    <input type="checkbox"
                                        aria-label={`Sélectionner ${q.id}`}
                                        checked={selectedIds.has(q.id)}
                                        onChange={() => toggleOne(q.id)}
                                        disabled={!!q.is_test}
                                        title={q.is_test ? "Déjà marqué TEST" : "Sélectionner pour reclasser en TEST"}
                                        data-testid={`row-cb-${q.id}`} />
                                </Td>
                                <Td>{new Date(q.created_at).toLocaleString("fr-FR")}</Td>
                                <Td>
                                    {[q.contact?.prenom, q.contact?.nom].filter(Boolean).join(" ") || "—"}
                                    {q.is_test && <span className="ml-2 inline-block text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300" data-testid={`badge-test-${q.id}`}>TEST</span>}
                                </Td>
                                <Td>
                                    <div>{q.contact?.email || "—"}</div>
                                    <div className="text-xs text-brand-ink/40">{q.contact?.telephone || ""}</div>
                                </Td>
                                <Td><span className="text-[#2A84C8] font-medium">{q.profile.profile_label}</span></Td>
                                <Td>{q.primary_name || "—"}</Td>
                                <Td>
                                    <button onClick={() => setSelected(q.id)} className="text-[#2A84C8] hover:underline" data-testid={`view-${q.id}`}>
                                        Voir
                                    </button>
                                </Td>
                            </tr>
                        ))}
                        {list.length === 0 && (
                            <tr><td colSpan={7} className="p-8 text-center text-brand-ink/40">Aucun questionnaire pour ce filtre.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {confirmReclassify && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="confirm-reclassify-modal">
                    <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-auto">
                        <h3 className="font-heading text-xl text-brand-ink mb-2">Reclasser en TEST — confirmation</h3>
                        <p className="text-sm text-brand-ink/70 mb-4">
                            Vous allez marquer <b>{selectedDocs.length}</b> questionnaire(s) comme <b>TEST</b>.
                            Ils disparaîtront de l'onglet « Réels » et ne seront plus comptés dans les statistiques commerciales.
                            Les demandes de rendez-vous liées seront également reclassées en TEST.
                            <br /><em>Aucune suppression — les données restent en base pour archive.</em>
                        </p>
                        <div className="border border-brand-ink/10 rounded-xl overflow-hidden mb-5">
                            <table className="w-full text-xs">
                                <thead className="bg-brand-beige/80 text-brand-ink/70">
                                    <tr>
                                        <th className="text-left p-2">Date</th>
                                        <th className="text-left p-2">Nom</th>
                                        <th className="text-left p-2">E-mail</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="reclassify-preview-list">
                                    {selectedDocs.map((q) => (
                                        <tr key={q.id} className="border-t border-brand-ink/5">
                                            <td className="p-2">{new Date(q.created_at).toLocaleString("fr-FR")}</td>
                                            <td className="p-2">{[q.contact?.prenom, q.contact?.nom].filter(Boolean).join(" ") || "—"}</td>
                                            <td className="p-2">{q.contact?.email || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button onClick={() => setConfirmReclassify(false)} className="ghost-btn" data-testid="confirm-reclassify-cancel">
                                Annuler
                            </button>
                            <button onClick={doReclassify} disabled={reclassifying}
                                className="primary-btn bg-amber-600 hover:bg-amber-700"
                                data-testid="confirm-reclassify-confirm">
                                {reclassifying ? "Reclassement…" : `Reclasser ${selectedDocs.length} questionnaire(s) en TEST`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {confirmDelete && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="confirm-delete-test-modal">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full">
                        <h3 className="font-heading text-xl text-brand-ink mb-3">Confirmer la suppression</h3>
                        <p className="text-sm text-brand-ink/70 mb-5">
                            Vous allez supprimer <b>{testDocs.length}</b> diagnostic(s) marqué(s) TEST. Cette action est irréversible et ne concerne pas les diagnostics historiques.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button onClick={() => setConfirmDelete(false)} className="ghost-btn" data-testid="confirm-delete-cancel">Annuler</button>
                            <button onClick={deleteAllTestDocs} className="primary-btn bg-red-600 hover:bg-red-700" data-testid="confirm-delete-confirm">Supprimer</button>
                        </div>
                    </div>
                </div>
            )}
            {selected && <QDetailModal qid={selected} onClose={() => setSelected(null)} />}
        </div>
    );
}

function QDetailModal({ qid, onClose }) {
    const [q, setQ] = useState(null);
    const [sending, setSending] = useState(false);
    const [resending, setResending] = useState(false);
    const [status, setStatus] = useState("nouveau");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const reloadDoc = async () => {
        const r = await api.get(`/admin/questionnaires/${qid}`);
        setQ(r.data);
        setStatus(r.data.status || "nouveau");
        setNotes(r.data.notes_conseiller || "");
    };
    useEffect(() => {
        reloadDoc().catch((e) => toast.error(formatApiError(e)));
        // eslint-disable-next-line
    }, [qid]);
    const send = async () => {
        setSending(true);
        try {
            const { data } = await api.post(`/admin/questionnaires/${qid}/email`);
            if (data?.dry_run) {
                toast.warning(`⚠ Envoi simulé — aucun e-mail transmis (mode DRY_RUN actif côté serveur, destinataire prévu : ${data.to})`);
            } else if (data?.sent && data?.email_id) {
                toast.success(`Email envoyé à ${data.to} (id ${data.email_id.slice(0, 8)}…)`);
            } else {
                toast.error("Envoi non confirmé par le fournisseur — réessayez.");
            }
            reloadDoc().catch(() => {});
        } catch (e) { toast.error(`Envoi échoué : ${formatApiError(e)}`); }
        finally { setSending(false); }
    };
    const resendAdvisor = async () => {
        setResending(true);
        try {
            const { data } = await api.post(`/admin/questionnaires/${qid}/resend-notification`);
            const n = data?.notification || {};
            if (n.dry_run) {
                toast.warning(`⚠ Envoi simulé — aucun e-mail transmis (mode DRY_RUN actif côté serveur, destinataire prévu : ${n.to})`);
            } else if (data?.sent && n.email_id) {
                toast.success(`Notification renvoyée à ${n.to} (id ${n.email_id.slice(0, 8)}…)`);
            } else if (n.reason) {
                toast.error(`Renvoi échoué : ${n.reason}`);
            } else {
                toast.error("Renvoi non confirmé — vérifiez la configuration conseiller.");
            }
            reloadDoc().catch(() => {});
        } catch (e) { toast.error(`Renvoi échoué : ${formatApiError(e)}`); }
        finally { setResending(false); }
    };
    const saveStatusNotes = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/questionnaires/${qid}/status_notes`, { status, notes_conseiller: notes });
            setSavedAt(new Date().toLocaleTimeString("fr-FR"));
            toast.success("Statut et notes enregistrés");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    const printClient  = () => { window.open(`/admin/print/${qid}?mode=client`, "_blank", "noopener"); };
    const printAdvisor = () => { window.open(`/admin/print/${qid}?mode=advisor`, "_blank", "noopener"); };
    const STATUSES = [
        ["nouveau", "Nouveau diagnostic"], ["a_contacter", "À contacter"],
        ["rdv_demande", "Rendez-vous demandé"], ["rdv_confirme", "Rendez-vous confirmé"],
        ["essai_realise", "Essai réalisé"], ["vente_conclue", "Vente conclue"], ["sans_suite", "Sans suite"],
    ];
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 print:relative print:inset-auto print:bg-white print:p-0" data-testid="q-detail-modal">
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-3xl w-full max-h-[90vh] overflow-auto print:max-h-none print:overflow-visible print:rounded-none print:shadow-none">
                <div className="flex justify-between items-start mb-4 print:hidden">
                    <h2 className="font-heading text-2xl font-semibold">Détail du questionnaire</h2>
                    <button onClick={onClose} className="text-brand-ink/50 hover:text-brand-ink" data-testid="close-modal" aria-label="Fermer">✕</button>
                </div>
                {!q ? <div>Chargement...</div> : (
                    <div className="space-y-4">
                        <div className="hidden print:block mb-4">
                            <p className="font-heading text-xl">Le Petit Matelassier — La Ciotat</p>
                            <p className="text-xs text-brand-ink/60">Diagnostic du {new Date(q.created_at).toLocaleString("fr-FR")}</p>
                            <p className="text-xs text-brand-ink/60">Contact boutique : 06 77 54 82 20 · lepetitmatelassier@gmail.com</p>
                        </div>
                        <div className="print-hide-client bg-brand-beige/40 rounded-2xl p-4 border border-brand-ink/10 space-y-3">
                            <label className="block">
                                <span className="block text-sm text-brand-ink/70 mb-1">Statut commercial</span>
                                <select value={status} onChange={(e) => setStatus(e.target.value)}
                                    className="w-full min-h-[44px] rounded-xl border border-brand-ink/15 px-3 bg-white"
                                    data-testid="status-select">
                                    {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className="block text-sm text-brand-ink/70 mb-1">Notes conseiller</span>
                                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={5000}
                                    className="w-full rounded-xl border border-brand-ink/15 px-3 py-2 bg-white"
                                    data-testid="notes-textarea" />
                            </label>
                            <div className="flex items-center gap-3 flex-wrap">
                                <button onClick={saveStatusNotes} disabled={saving} className="primary-btn" data-testid="save-status-notes-btn">
                                    {saving ? "Enregistrement…" : "Enregistrer"}
                                </button>
                                {savedAt && <span className="text-xs text-green-700" data-testid="saved-indicator">Enregistré à {savedAt}</span>}
                                {q.status_updated_at && !savedAt && <span className="text-xs text-brand-ink/50">Dernière modif : {new Date(q.status_updated_at).toLocaleString("fr-FR")}</span>}
                            </div>
                        </div>
                        <DetailSection title="Client">
                            {[q.contact?.prenom, q.contact?.nom].filter(Boolean).join(" ") || "Anonyme"}<br />
                            <span className="text-sm text-brand-ink/60">{q.contact?.email || "—"} · {q.contact?.telephone || "—"}</span>
                        </DetailSection>
                        <DetailSection title="Profil">
                            <p className="text-[#2A84C8] font-semibold text-lg">{q.profile.profile_label}</p>
                            <p className="text-sm text-brand-ink/60">{q.answers?.sleepers?.length === 2 ? "Deux dormeurs" : "Un dormeur"}</p>
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3 text-sm">
                                {Object.entries(q.profile.scores).map(([k, v]) => (
                                    <li key={k} className="flex justify-between border-b border-brand-ink/10 py-1">
                                        <span className="capitalize">{k}</span><span className="font-semibold">{v} %</span>
                                    </li>
                                ))}
                            </ul>
                        </DetailSection>
                        <DetailSection title="Matelas recommandé">
                            <p className="font-semibold">{q.recommendation_details?.primary?.name || "—"} {q.recommendation_details?.primary?.adequation?.score != null && <span className="text-[#2A84C8]"> — {q.recommendation_details.primary.adequation.score}%</span>}</p>
                            <p className="text-sm text-brand-ink/60">Alternative : {q.recommendation_details?.alternative?.name || "—"}</p>
                            {q.recommendation_details?.hr3_plus_card && <p className="text-sm text-amber-700 mt-1">Solution HR3 Plus proposée</p>}
                        </DetailSection>
                        <DetailSection title="Raisons">
                            <ul className="text-sm space-y-1">{(q.reasons || []).map((r, i) => <li key={i}>• {r}</li>)}</ul>
                        </DetailSection>
                        {q.answers?.other_texts && (
                            <DetailSection title="Précisions « Autre »">
                                {q.answers.other_texts.change_reason && <p className="text-sm">Raison : {q.answers.other_texts.change_reason}</p>}
                                {q.answers.other_texts.discomfort && Object.entries(q.answers.other_texts.discomfort).map(([k, v]) => (
                                    <p key={k} className="text-sm">Gêne dormeur {Number(k) + 1} : {v}</p>
                                ))}
                            </DetailSection>
                        )}
                        {q.answers?.dimension_custom && (
                            <DetailSection title="Dimension sur-mesure">
                                {q.answers.dimension_custom.largeur} × {q.answers.dimension_custom.longueur} cm
                            </DetailSection>
                        )}
                        <div className="print-hide-client">
                            <DetailSection title="Réponses détaillées">
                                <AnswersReadable answers={q.answers} />
                            </DetailSection>
                        </div>
                        <div className="print-hide-client">
                            <DetailSection title="Notification conseiller (automatique)">
                                {(() => {
                                    const n = q.notification;
                                    if (!n) {
                                        return <p className="text-sm text-amber-700" data-testid="notif-status">
                                            Aucune notification enregistrée pour ce questionnaire. Utilisez « Renvoyer » ci-dessous.
                                        </p>;
                                    }
                                    if (n.dry_run) {
                                        return <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="notif-status">
                                            ⚠ <strong>Envoi simulé — aucun e-mail transmis</strong> (mode DRY_RUN actif côté serveur).
                                            Destinataire prévu : <strong>{n.to || "—"}</strong>
                                            {n.at && <> · {new Date(n.at).toLocaleString("fr-FR")}</>}
                                            {n.simulated_id && <> · id simulé <code className="text-xs">{n.simulated_id}</code></>}
                                        </p>;
                                    }
                                    if (n.sent) {
                                        return <p className="text-sm text-green-700" data-testid="notif-status">
                                            ✓ Envoyé à <strong>{n.to || "—"}</strong> le {n.at ? new Date(n.at).toLocaleString("fr-FR") : "?"}
                                            {n.email_id && <> · id <code className="text-xs">{n.email_id.slice(0, 8)}…</code></>}
                                        </p>;
                                    }
                                    return <p className="text-sm text-red-700" data-testid="notif-status">
                                        ✗ Non envoyé — raison : <strong>{n.reason || (n.blocked_by_test_mode ? "blocked_by_test_mode" : "inconnue")}</strong>
                                        {n.to && <> · destinataire prévu : {n.to}</>}
                                        {n.at && <> · {new Date(n.at).toLocaleString("fr-FR")}</>}
                                    </p>;
                                })()}
                                <button onClick={resendAdvisor} disabled={resending}
                                    className="ghost-btn mt-2" data-testid="resend-advisor-btn">
                                    {resending ? "Renvoi…" : "Renvoyer la notification conseiller"}
                                </button>
                            </DetailSection>
                        </div>
                        <div className="flex gap-3 justify-end pt-4 border-t border-brand-ink/10 print:hidden flex-wrap">
                            <button onClick={printClient}  className="ghost-btn" data-testid="print-client-btn">Imprimer le résultat client</button>
                            <button onClick={printAdvisor} className="ghost-btn" data-testid="print-advisor-btn">Imprimer la fiche conseiller</button>
                            {q.contact?.email && (
                                <button onClick={send} disabled={sending} className="primary-btn" data-testid="send-email-btn">
                                    <Mail className="w-4 h-4" /> {sending ? "Envoi..." : "Envoyer par email"}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function QuestionnaireDetail() { return null; }

const EMPTY_MATTRESS = {
    name: "", range: "", technology: "", description: "",
    attributes: { accueil: 60, soutien: 60, aeration: 60, independance: 60, mouvement: 60 },
    supports_differentiated: false, features: [], active: true,
};

function MattressesAdmin() {
    const [list, setList] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/admin/mattresses").then((r) => setList(r.data));
    useEffect(() => { load(); }, []);
    const del = async (id) => {
        if (!window.confirm("Supprimer ce matelas ?")) return;
        await api.delete(`/admin/mattresses/${id}`);
        load(); toast.success("Supprimé");
    };
    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="font-heading text-3xl font-semibold text-brand-ink">Catalogue matelas</h1>
                <button onClick={() => setEditing({ ...EMPTY_MATTRESS })} className="primary-btn" data-testid="add-mattress-btn">
                    <Plus className="w-4 h-4" /> Ajouter un matelas
                </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                {list.map((m) => (
                    <div key={m.id} className="bg-white rounded-2xl p-5 border border-brand-ink/10" data-testid={`mattress-${m.id}`}>
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="font-heading text-lg font-semibold">{m.name}</p>
                                <p className="text-xs text-brand-ink/50">{m.range} · {m.active ? "Actif" : "Inactif"}</p>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => setEditing(m)} className="text-sm text-[#2A84C8] hover:underline">Modifier</button>
                                <button onClick={() => del(m.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                            </div>
                        </div>
                        <p className="text-sm text-brand-ink/70 mt-2">{m.technology}</p>
                        <div className="grid grid-cols-5 gap-2 mt-3 text-xs">
                            {Object.entries(m.attributes).map(([k, v]) => (
                                <div key={k} className="text-center">
                                    <div className="text-brand-ink/50 capitalize">{k.slice(0, 4)}</div>
                                    <div className="font-semibold text-[#2A84C8]">{v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {editing && <MattressForm mattress={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
}

function MattressForm({ mattress, onClose, onSaved }) {
    const [f, setF] = useState(mattress);
    const [saving, setSaving] = useState(false);
    const attr = (k, v) => setF({ ...f, attributes: { ...f.attributes, [k]: Number(v) } });
    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...f, features: typeof f.features === "string" ? f.features.split("\n").map((x) => x.trim()).filter(Boolean) : f.features };
            if (mattress.id) await api.put(`/admin/mattresses/${mattress.id}`, payload);
            else await api.post("/admin/mattresses", payload);
            toast.success("Enregistré");
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-auto">
                <h2 className="font-heading text-2xl font-semibold mb-4">
                    {mattress.id ? "Modifier le matelas" : "Nouveau matelas"}
                </h2>
                <div className="grid md:grid-cols-2 gap-3">
                    <FormField label="Nom" value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="m-name" />
                    <FormField label="Gamme" value={f.range} onChange={(v) => setF({ ...f, range: v })} testid="m-range" />
                </div>
                <FormField label="Technologie" value={f.technology} onChange={(v) => setF({ ...f, technology: v })} testid="m-tech" />
                <label className="block mt-3">
                    <span className="block text-sm text-brand-ink/70 mb-1">Description</span>
                    <textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                        className="w-full rounded-xl border border-brand-ink/15 p-3 focus:outline-none focus:ring-2 focus:ring-[#2A84C8]/30" data-testid="input-m-desc" />
                </label>
                <label className="block mt-3">
                    <span className="block text-sm text-brand-ink/70 mb-1">Caractéristiques (une par ligne)</span>
                    <textarea rows={4} value={Array.isArray(f.features) ? f.features.join("\n") : f.features}
                        onChange={(e) => setF({ ...f, features: e.target.value })}
                        className="w-full rounded-xl border border-brand-ink/15 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A84C8]/30" data-testid="input-m-features" />
                </label>
                <div className="mt-4">
                    <p className="text-sm text-brand-ink/70 mb-2">Attributs (0-100)</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        {Object.entries(f.attributes).map(([k, v]) => (
                            <label key={k} className="block">
                                <span className="block text-xs capitalize mb-1">{k}</span>
                                <input type="number" min="0" max="100" value={v} onChange={(e) => attr(k, e.target.value)}
                                    className="w-full min-h-[40px] rounded-lg border border-brand-ink/15 px-2 text-center" data-testid={`m-attr-${k}`} />
                            </label>
                        ))}
                    </div>
                </div>
                <div className="mt-4 flex items-center gap-4">
                    <label className="inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={f.supports_differentiated} onChange={(e) => setF({ ...f, supports_differentiated: e.target.checked })} />
                        Supporte confort différencié (réversible/dual)
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
                        Actif
                    </label>
                </div>
                <div className="flex gap-3 justify-end pt-6 border-t border-brand-ink/10 mt-6">
                    <button onClick={onClose} className="ghost-btn">Annuler</button>
                    <button onClick={save} disabled={saving} className="primary-btn" data-testid="m-save-btn">
                        {saving ? "Enregistrement..." : "Enregistrer"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function CoefficientsAdmin() {
    const [c, setC] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/coefficients").then((r) => setC(r.data)).catch((e) => toast.error(formatApiError(e)));
    }, []);

    const set = (path, value) => {
        setC((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const keys = path.split(".");
            let obj = next;
            for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
            obj[keys[keys.length - 1]] = value === "" ? "" : Number(value);
            return next;
        });
    };

    const save = async () => {
        setSaving(true);
        try {
            // Coerce any stray "" back to numbers before sending
            const clean = JSON.parse(JSON.stringify(c), (k, v) => (v === "" ? 0 : v));
            await api.put("/admin/coefficients", clean);
            setC(clean);
            toast.success("Coefficients enregistrés");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const reset = async () => {
        if (!window.confirm("Restaurer les valeurs par défaut ? Toutes les modifications seront écrasées.")) return;
        try {
            const r = await api.post("/admin/coefficients/reset");
            setC(r.data.data);
            toast.success("Valeurs par défaut restaurées");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (!c) return <div className="text-brand-ink/60">Chargement...</div>;

    const SENS_LABELS = {
        enveloppante: "Très enveloppante", moelleuse: "Moelleuse", equilibree: "Équilibrée",
        tonique: "Tonique", ferme: "Ferme", inconnu: "Je ne sais pas",
    };
    const POS_LABELS = { dos: "Sur le dos", cote: "Sur le côté", ventre: "Sur le ventre", change: "Change souvent", inconnu: "Ne sait pas" };
    const HEAT_LABELS = { rarement: "Rarement", parfois: "Parfois", souvent: "Souvent", tres_souvent: "Très souvent" };
    const MOVE_LABELS = { pas: "Pas importante", peu: "Peu importante", assez: "Assez importante", tres: "Très importante" };
    const PART_LABELS = { pas: "Pas du tout", peu: "Un peu", souvent: "Souvent", beaucoup: "Beaucoup" };

    return (
        <div data-testid="coefficients-admin" className="space-y-6 max-w-5xl">
            <div className="flex justify-between items-start">
                <div>
                    <h1 className="font-heading text-3xl font-semibold text-brand-ink">Coefficients du moteur</h1>
                    <p className="text-sm text-brand-ink/60 mt-1 max-w-2xl">
                        Réglez ici l'influence de chaque question sur le profil de confort et sur le calcul
                        de compatibilité. Les valeurs sont sur une échelle de 0 à 100 sauf mention contraire.
                    </p>
                </div>
                <div className="flex gap-2 shrink-0">
                    <button onClick={reset} className="ghost-btn" data-testid="coef-reset">
                        <RotateCcw className="w-4 h-4" /> Restaurer les valeurs par défaut
                    </button>
                    <button onClick={save} disabled={saving} className="primary-btn" data-testid="coef-save">
                        {saving ? "Enregistrement..." : "Enregistrer les modifications"}
                    </button>
                </div>
            </div>

            <Section title="Sensation recherchée par le client" hint="Pour chaque choix, l'attente d'enveloppement et de tonicité (0-100).">
                <Table headers={["Choix", "Enveloppement souhaité", "Tonicité souhaitée"]}>
                    {Object.keys(SENS_LABELS).map((k) => (
                        <tr key={k} className="border-t border-brand-ink/5">
                            <Td>{SENS_LABELS[k]}</Td>
                            <TdInput value={c.sensation?.[k]?.env} onChange={(v) => set(`sensation.${k}.env`, v)} testid={`sens-${k}-env`} />
                            <TdInput value={c.sensation?.[k]?.ton} onChange={(v) => set(`sensation.${k}.ton`, v)} testid={`sens-${k}-ton`} />
                        </tr>
                    ))}
                </Table>
            </Section>

            <Section title="Niveau d'enveloppement 1 → 5" hint="Valeur produite par la réponse au curseur 1-5. Une réponse 3 doit rester proche de 50.">
                <div className="grid grid-cols-5 gap-3">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <NumField key={n} label={`${n}/5`} value={c.envelopment_scale?.[n]} onChange={(v) => set(`envelopment_scale.${n}`, v)} testid={`env-scale-${n}`} />
                    ))}
                </div>
                <div className="mt-3">
                    <NumField
                        label="Poids du curseur 1-5 dans l'accueil (0 à 1)"
                        step="0.05" min="0" max="1"
                        value={c.env_scale_alpha}
                        onChange={(v) => set("env_scale_alpha", v)}
                        testid="env-scale-alpha"
                    />
                </div>
            </Section>

            <Section title="Facilité de mouvement — importance" hint="Poids donné selon la réponse du client à Q6.">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.keys(MOVE_LABELS).map((k) => (
                        <NumField key={k} label={MOVE_LABELS[k]} value={c.movement_ease?.[k]} onChange={(v) => set(`movement_ease.${k}`, v)} testid={`move-${k}`} />
                    ))}
                </div>
            </Section>

            <Section title="Sensibilité à la chaleur — aération" hint="Valeur souhaitée d'aération + multiplicateur du poids de la catégorie (Q7).">
                <Table headers={["Réponse", "Aération souhaitée (0-100)", "Multiplicateur de poids (0-2)"]}>
                    {Object.keys(HEAT_LABELS).map((k) => (
                        <tr key={k} className="border-t border-brand-ink/5">
                            <Td>{HEAT_LABELS[k]}</Td>
                            <TdInput value={c.heat?.[k]?.want} onChange={(v) => set(`heat.${k}.want`, v)} testid={`heat-${k}-want`} />
                            <TdInput step="0.05" min="0" max="2" value={c.heat?.[k]?.weight_mult} onChange={(v) => set(`heat.${k}.weight_mult`, v)} testid={`heat-${k}-mult`} />
                        </tr>
                    ))}
                </Table>
            </Section>

            <Section title="Mouvements du partenaire — indépendance de couchage" hint="Besoin d'indépendance selon la réponse (Q8).">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.keys(PART_LABELS).map((k) => (
                        <NumField key={k} label={PART_LABELS[k]} value={c.partner?.[k]} onChange={(v) => set(`partner.${k}`, v)} testid={`part-${k}`} />
                    ))}
                </div>
            </Section>

            <Section title="Position de sommeil — besoin de soutien" hint="Cible de soutien selon la position principale.">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {Object.keys(POS_LABELS).map((k) => (
                        <NumField key={k} label={POS_LABELS[k]} value={c.position_soutien?.[k]} onChange={(v) => set(`position_soutien.${k}`, v)} testid={`pos-${k}`} />
                    ))}
                </div>
            </Section>

            <Section title="Morphologie (IMC interne)" hint="Cible de soutien selon la morphologie. Non affiché au client.">
                <Table headers={["Catégorie", "IMC max", "Soutien cible"]}>
                    {["low", "normal", "high", "very_high"].map((k) => (
                        <tr key={k} className="border-t border-brand-ink/5">
                            <Td>{{ low: "Léger", normal: "Standard", high: "Corpulent", very_high: "Très corpulent" }[k]}</Td>
                            <TdInput step="0.1" value={c.imc_thresholds?.[k]?.max} onChange={(v) => set(`imc_thresholds.${k}.max`, v)} testid={`imc-${k}-max`} />
                            <TdInput value={c.imc_thresholds?.[k]?.soutien} onChange={(v) => set(`imc_thresholds.${k}.soutien`, v)} testid={`imc-${k}-soutien`} />
                        </tr>
                    ))}
                </Table>
            </Section>

            <Section title="Pondération des catégories dans le score" hint="Poids relatif (0-1). Total conseillé ≈ 1.0. L'aération est modulée par la chaleur.">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {["sensation", "position", "soutien", "mouvement", "aeration"].map((k) => (
                        <NumField key={k} step="0.01" min="0" max="1"
                            label={k[0].toUpperCase() + k.slice(1)}
                            value={c.category_weights_base?.[k]}
                            onChange={(v) => set(`category_weights_base.${k}`, v)}
                            testid={`cw-${k}`} />
                    ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <NumField step="0.01" min="0" max="0.5"
                        label="Bonus mouvement si change/mvt très important"
                        value={c.mouvement_weight_bonus_change}
                        onChange={(v) => set("mouvement_weight_bonus_change", v)}
                        testid="mvt-bonus" />
                    <NumField step="0.01" min="0" max="0.5"
                        label="Poids indépendance (si partenaire actif)"
                        value={c.independance_weight}
                        onChange={(v) => set("independance_weight", v)}
                        testid="indep-weight" />
                </div>
            </Section>

            <Section title="Compatibilité" hint="Formule sim = 100 − k × |besoin − matelas|, plafonnée par le cap.">
                <div className="grid grid-cols-2 gap-3">
                    <NumField step="0.05" min="0.5" max="3" label="Pente k" value={c.sim_k} onChange={(v) => set("sim_k", v)} testid="sim-k" />
                    <NumField min="80" max="100" label="Plafond de similarité (cap)" value={c.sim_cap} onChange={(v) => set("sim_cap", v)} testid="sim-cap" />
                </div>
            </Section>

            <Section title="Mode couple" hint="Agrégation D1/D2 : score = α × moyenne + β × minimum.">
                <div className="grid grid-cols-2 gap-3">
                    <NumField step="0.05" min="0" max="1" label="α · moyenne" value={c.couple_alpha_mean} onChange={(v) => set("couple_alpha_mean", v)} testid="couple-alpha" />
                    <NumField step="0.05" min="0" max="1" label="β · minimum" value={c.couple_beta_min} onChange={(v) => set("couple_beta_min", v)} testid="couple-beta" />
                </div>
            </Section>

            <Section title="Règle HR3 Plus" hint="Écart déclenchant la suggestion HR3 Plus (sur /10).">
                <div className="grid grid-cols-2 gap-3">
                    <NumField min="1" max="10" label="Écart minimum sur un axe (/10)" value={c.hr3_plus_rules?.single_axis_gap_10} onChange={(v) => set("hr3_plus_rules.single_axis_gap_10", v)} testid="hr3-single" />
                    <NumField min="1" max="10" label="Écart minimum sur les deux axes (/10)" value={c.hr3_plus_rules?.double_axis_gap_10} onChange={(v) => set("hr3_plus_rules.double_axis_gap_10", v)} testid="hr3-double" />
                </div>
            </Section>

            <Section title="Proximité des scores" hint="Seuil sous lequel les 2 premiers modèles sont considérés très proches.">
                <NumField step="0.5" min="0" max="10" label="Écart maximum (points de compat.)" value={c.close_call_threshold} onChange={(v) => set("close_call_threshold", v)} testid="close-call" />
            </Section>

            <div className="flex justify-end gap-2 pt-4 border-t border-brand-ink/10">
                <button onClick={reset} className="ghost-btn"><RotateCcw className="w-4 h-4" /> Restaurer les valeurs par défaut</button>
                <button onClick={save} disabled={saving} className="primary-btn">
                    {saving ? "Enregistrement..." : "Enregistrer les modifications"}
                </button>
            </div>
        </div>
    );
}

function Section({ title, hint, children }) {
    return (
        <div className="bg-white rounded-2xl border border-brand-ink/10 p-5">
            <h2 className="font-heading text-lg font-semibold text-brand-ink mb-1">{title}</h2>
            {hint && <p className="text-xs text-brand-ink/50 mb-4">{hint}</p>}
            {children}
        </div>
    );
}

function Table({ headers, children }) {
    return (
        <div className="overflow-auto">
            <table className="w-full text-sm">
                <thead className="text-brand-ink/60">
                    <tr>{headers.map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr>
                </thead>
                <tbody>{children}</tbody>
            </table>
        </div>
    );
}

function TdInput({ value, onChange, step = "1", min = "0", max = "100", testid }) {
    return (
        <td className="px-3 py-2">
            <input
                type="number"
                step={step} min={min} max={max}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-28 min-h-[40px] rounded-lg border border-brand-ink/15 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A84C8]/30"
                data-testid={`input-${testid}`}
            />
        </td>
    );
}

function NumField({ label, value, onChange, step = "1", min = "0", max = "100", testid }) {
    return (
        <label className="block">
            <span className="block text-xs text-brand-ink/60 mb-1">{label}</span>
            <input
                type="number"
                step={step} min={min} max={max}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full min-h-[42px] rounded-lg border border-brand-ink/15 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A84C8]/30"
                data-testid={`input-${testid}`}
            />
        </label>
    );
}

function PricingAdmin() {
    const [pricing, setPricing] = useState(null);
    const [mattresses, setMattresses] = useState([]);
    const [saving, setSaving] = useState(false);
    const DIMENSIONS = ["80 × 190", "80 × 200", "90 × 190", "90 × 200", "100 × 190", "100 × 200", "120 × 190", "120 × 200", "140 × 190", "140 × 200", "160 × 200", "180 × 200", "200 × 200"];

    useEffect(() => {
        Promise.all([
            api.get("/admin/pricing").then((r) => r.data),
            api.get("/admin/mattresses").then((r) => r.data),
        ])
            .then(([p, m]) => { setPricing(p); setMattresses(m); })
            .catch((e) => toast.error(formatApiError(e)));
    }, []);

    const setPrice = (mid, dim, val) => {
        const next = { ...pricing };
        next.prices = { ...next.prices, [mid]: { ...(next.prices[mid] || {}) } };
        if (val === "" || val === null) {
            delete next.prices[mid][dim];
        } else {
            next.prices[mid][dim] = Number(val);
        }
        setPricing(next);
    };

    const setMultiplier = (val) => {
        setPricing({ ...pricing, special_multiplier: Number(val) });
    };

    const toggleSpecialRule = (mid) => {
        const list = pricing.apply_special_rule_for || [];
        const next = list.includes(mid) ? list.filter((x) => x !== mid) : [...list, mid];
        setPricing({ ...pricing, apply_special_rule_for: next });
    };

    const setFallback = (dim, val) => {
        const next = { ...pricing, special_fallback: { ...(pricing.special_fallback || {}) } };
        if (!val) delete next.special_fallback[dim];
        else next.special_fallback[dim] = val;
        setPricing(next);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/pricing", pricing);
            toast.success("Tarifs enregistrés");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const reset = async () => {
        if (!window.confirm("Réinitialiser aux tarifs par défaut ?")) return;
        const r = await api.post("/admin/pricing/reset");
        setPricing(r.data.data);
        toast.success("Tarifs réinitialisés");
    };

    if (!pricing) return <div className="text-brand-ink/60">Chargement...</div>;

    return (
        <div data-testid="pricing-admin">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h1 className="font-heading text-3xl font-semibold text-brand-ink">Tarifs internes</h1>
                    <p className="text-sm text-brand-ink/60 mt-1 max-w-xl">
                        Ces prix ne sont jamais affichés au client. Ils servent uniquement au calcul interne
                        de l'adéquation au budget. Laissez une case vide pour indiquer « prix non renseigné ».
                    </p>
                </div>
                <div className="flex gap-2">
                    <button onClick={reset} className="ghost-btn" data-testid="pricing-reset"><RotateCcw className="w-4 h-4" /> Réinitialiser</button>
                    <button onClick={save} disabled={saving} className="primary-btn" data-testid="pricing-save">
                        {saving ? "Enregistrement..." : "Enregistrer"}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-brand-ink/10 overflow-auto">
                <table className="w-full text-sm">
                    <thead className="bg-brand-beige">
                        <tr>
                            <th className="text-left px-4 py-3 font-medium">Modèle</th>
                            {DIMENSIONS.map((d) => <th key={d} className="text-center px-2 py-3 font-medium whitespace-nowrap">{d}</th>)}
                            <th className="text-center px-3 py-3 font-medium whitespace-nowrap">Règle +20 %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mattresses.map((m) => (
                            <tr key={m.id} className="border-t border-brand-ink/5">
                                <td className="px-4 py-3 font-medium text-brand-ink">{m.name}</td>
                                {DIMENSIONS.map((d) => (
                                    <td key={d} className="px-2 py-2 text-center">
                                        <input
                                            type="number"
                                            min="0"
                                            step="10"
                                            value={pricing.prices?.[m.id]?.[d] ?? ""}
                                            onChange={(e) => setPrice(m.id, d, e.target.value)}
                                            className="w-24 min-h-[40px] rounded-lg border border-brand-ink/15 px-2 text-center text-sm focus:outline-none focus:ring-2 focus:ring-[#2A84C8]/30"
                                            data-testid={`price-${m.id}-${d.replace(/\s|×/g, "")}`}
                                        />
                                    </td>
                                ))}
                                <td className="text-center px-3 py-2">
                                    <input
                                        type="checkbox"
                                        checked={(pricing.apply_special_rule_for || []).includes(m.id)}
                                        onChange={() => toggleSpecialRule(m.id)}
                                        data-testid={`special-rule-${m.id}`}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-6 bg-white rounded-2xl border border-brand-ink/10 p-6">
                <h2 className="font-heading text-xl font-semibold text-brand-ink mb-4">Règle des dimensions spéciales</h2>
                <p className="text-sm text-brand-ink/60 mb-4 max-w-2xl">
                    Pour les modèles avec « Règle +20 % » activée, une dimension absente de leur grille utilise
                    la dimension standard supérieure ci-dessous, multipliée par le coefficient.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className="block">
                        <span className="block text-sm text-brand-ink/70 mb-1">Coefficient</span>
                        <input
                            type="number"
                            step="0.01"
                            min="1"
                            value={pricing.special_multiplier ?? 1.2}
                            onChange={(e) => setMultiplier(e.target.value)}
                            className="w-full min-h-[44px] rounded-xl border border-brand-ink/15 px-3 focus:outline-none focus:ring-2 focus:ring-[#2A84C8]/30"
                            data-testid="special-multiplier"
                        />
                    </label>
                </div>
                <div className="mt-4">
                    <p className="text-sm text-brand-ink/70 mb-2">Correspondances dimension spéciale → dimension standard :</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(pricing.special_fallback || {}).map(([sp, std]) => (
                            <div key={sp} className="flex items-center gap-2 text-sm">
                                <span className="w-24 text-brand-ink/70">{sp}</span>
                                <span className="text-brand-ink/40">→</span>
                                <select value={std} onChange={(e) => setFallback(sp, e.target.value)} className="min-h-[40px] rounded-lg border border-brand-ink/15 px-2" data-testid={`fallback-${sp.replace(/\s|×/g, "")}`}>
                                    {DIMENSIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                                </select>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function AppointmentsList() {
    const [list, setList] = useState([]);
    useEffect(() => { api.get("/admin/appointments").then((r) => setList(r.data)); }, []);
    return (
        <div>
            <h1 className="font-heading text-3xl font-semibold text-brand-ink mb-6">Demandes de rendez-vous</h1>
            <div className="bg-white rounded-2xl border border-brand-ink/10 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-brand-beige text-brand-ink/70">
                        <tr><Th>Date</Th><Th>Prénom</Th><Th>Téléphone</Th><Th>Email</Th><Th>Créneau</Th></tr>
                    </thead>
                    <tbody data-testid="appointments-table-body">
                        {list.map((a) => (
                            <tr key={a.id} className="border-t border-brand-ink/5">
                                <Td>{new Date(a.created_at).toLocaleString("fr-FR")}</Td>
                                <Td>{a.prenom}</Td><Td>{a.telephone}</Td><Td>{a.email || "—"}</Td><Td>{a.creneau || "—"}</Td>
                            </tr>
                        ))}
                        {list.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-brand-ink/40">Aucune demande pour le moment.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function NotificationsAdmin() {
    const [cfg, setCfg] = useState({ email: "", enabled: false });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/admin/advisor-notify")
            .then((r) => setCfg(r.data || { email: "", enabled: false }))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);
    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/advisor-notify", cfg);
            toast.success("Notification enregistrée");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    if (loading) return <div className="text-brand-ink/60">Chargement...</div>;
    return (
        <div data-testid="notifications-admin" className="max-w-xl">
            <h1 className="font-heading text-3xl font-semibold text-brand-ink mb-2">Notifications conseiller</h1>
            <p className="text-sm text-brand-ink/60 mb-6">
                Un e-mail complet (coordonnées, questionnaire, scores, tarifs internes) est envoyé
                à cette adresse à chaque validation d'un diagnostic. Cette adresse n'est jamais
                exposée côté client.
            </p>
            <div className="bg-white rounded-2xl border border-brand-ink/10 p-6 space-y-4">
                <label className="block">
                    <span className="block text-sm text-brand-ink/70 mb-1">Adresse e-mail destinataire</span>
                    <input
                        type="email"
                        value={cfg.email || ""}
                        onChange={(e) => setCfg({ ...cfg, email: e.target.value })}
                        className="w-full min-h-[48px] rounded-xl border border-brand-ink/15 px-3 focus:outline-none focus:ring-2 focus:ring-[#2A84C8]/30"
                        placeholder="conseiller@petitmatelassier.fr"
                        data-testid="advisor-email-input"
                    />
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={!!cfg.enabled}
                        onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                        data-testid="advisor-enabled"
                    />
                    Activer l'envoi automatique après chaque diagnostic
                </label>
                <div className="flex justify-end pt-2">
                    <button onClick={save} disabled={saving} className="primary-btn" data-testid="advisor-save">
                        {saving ? "Enregistrement..." : "Enregistrer"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Th({ children }) { return <th className="text-left px-4 py-3 font-medium">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3 align-top text-brand-ink">{children}</td>; }
function DetailSection({ title, children }) {
    return (
        <div>
            <h3 className="text-sm uppercase tracking-wider text-brand-ink/50 mb-2">{title}</h3>
            <div>{children}</div>
        </div>
    );
}
function FormField({ label, value, onChange, testid }) {
    return (
        <label className="block mt-3">
            <span className="block text-sm text-brand-ink/70 mb-1">{label}</span>
            <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
                className="w-full min-h-[44px] rounded-xl border border-brand-ink/15 px-3 focus:outline-none focus:ring-2 focus:ring-[#2A84C8]/30" data-testid={`input-${testid}`} />
        </label>
    );
}
